// deploy-crash-recovery.ts
//
// Split off deployment-adapter.ts to stay ≤200 lines (test 43-44). Crash
// recovery after a deploy/migration operation whose completion status is
// uncertain — MUST probe reality before any retry decision (git_commit had
// its own reconciler for the same rule; R-25 removed it as unused, so this
// module is now the only place that rule lives, deploy-kind operations only).
export interface CrashRecoveryProbeInput {
  operationKind: string;
  nonIdempotent: boolean;
  /** Injected probe of REAL environment state — v1 has no deploy-specific
   * observer, so callers MUST supply one; omitting it means "cannot probe",
   * not "assume safe". */
  probeRealState?: () => Promise<{ alreadyApplied: boolean } | undefined>;
}

export type CrashRecoveryAction = "retry_safe" | "complete_receipt" | "needs_human";

export interface CrashRecoveryDecision {
  action: CrashRecoveryAction;
  reason: string;
}

/**
 * Test 43: crash after a remote side effect succeeded (or maybe-succeeded)
 * must probe REALITY before any retry — never trust "the process died" as
 * "the effect didn't happen". Test 44: a non-idempotent operation (typically
 * migration) with an UNCERTAIN probe result must NOT be retried — only
 * `needs_human`, because retrying could apply it twice.
 */
export async function decideCrashRecoveryAction(
  input: CrashRecoveryProbeInput,
): Promise<CrashRecoveryDecision> {
  if (input.probeRealState === undefined) {
    return {
      action: "needs_human",
      reason: `Không có probe thực tại cho operation_kind="${input.operationKind}" ⇒ không được đoán, cần người xác nhận.`,
    };
  }
  const probe = await input.probeRealState();
  if (probe === undefined) {
    return {
      action: "needs_human",
      reason:
        "Probe thực tại không kết luận được (uncertain) ⇒ cần người xác nhận, không tự retry.",
    };
  }
  if (probe.alreadyApplied) {
    return {
      action: "complete_receipt",
      reason: "Probe xác nhận side effect ĐÃ xảy ra ⇒ chỉ ghi receipt, không chạy lại.",
    };
  }
  if (input.nonIdempotent) {
    return {
      action: "needs_human",
      reason:
        "Operation non-idempotent (vd migration) và probe không xác nhận chắc chắn CHƯA áp dụng " +
        "⇒ KHÔNG retry mù (test 44), cần người xác nhận trước.",
    };
  }
  return {
    action: "retry_safe",
    reason: "Probe xác nhận CHƯA xảy ra và operation idempotent-an-toàn ⇒ retry được.",
  };
}
